import http from 'utils/http';
import {
  getAgentDataSetsUrls,
  getAgentDataSetUrls,
  getAgentStatusUrl,
  getAgentsUrl,
  getBusinessAgentsUrl,
  getAgentTemplatesUrl,
  getAgentUrl,
  getAmbientSoundsUrl,
  getLiveKitTokenUrl,
  getPublicAgentWidgetConfigUrl,
  getAgentPronunciationRulesUrl,
  getAgentPronunciationRuleUrl,
  getThinkingSoundsUrl
} from './apiUrls';
import { AgentResponse, AmbientSound, AgentStatusResponse, PronunciationRule, ThinkingSound } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { DatasetResponse } from '../setting/knowledgeBase/types';

export function getAgents(
  workspaceId: string,
  params?: Record<string, any>
): Promise<{ data: AgentResponse[]; meta: MetaApiResponse }> {
  return http.get(getAgentsUrl(workspaceId), params);
}

export function getBusinessAgents(
  params?: Record<string, any>
): Promise<{ data: AgentResponse[]; meta: MetaApiResponse }> {
  return http.get(getBusinessAgentsUrl(), params);
}

export function getAmbientSounds(): Promise<{ data: AmbientSound[] }> {
  return http.get(getAmbientSoundsUrl());
}

export function getThinkingSounds(): Promise<{ data: ThinkingSound[] }> {
  return http.get(getThinkingSoundsUrl());
}

export function getAgent(workspaceId: string, id: string): Promise<AgentResponse> {
  return http.get(getAgentUrl(workspaceId, id));
}

export function updateAgent(workspaceId: string, id: string, payload: any): Promise<AgentResponse> {
  return http.patch(getAgentUrl(workspaceId, id), payload);
}

export function getAgentStatus(workspaceId: string, id: string): Promise<AgentStatusResponse> {
  return http.get(getAgentStatusUrl(workspaceId, id));
}

export function deleteAgent(workspaceId: string, id: number): Promise<AgentResponse> {
  return http.delete(getAgentUrl(workspaceId, id), {}, { skipConfirm: true });
}

export function updateAgentConfig(workspaceId: string, id: string, payload: any): Promise<AgentResponse> {
  return http.patch(getAgentUrl(workspaceId, id), payload);
}

export function getAgentTemplates(
  workspaceId: string,
  params: Record<string, any>
): Promise<{ data: AgentResponse[]; meta: MetaApiResponse }> {
  return http.get(getAgentTemplatesUrl(workspaceId), params);
}

export function cloneAgent(workspaceId: string, payload: { agent_template_id?: number }): Promise<AgentResponse> {
  return http.post(getAgentsUrl(workspaceId), payload);
}

export function getDataSets(
  workspaceId: string,
  agentId: string,
  params?: Record<string, any>
): Promise<{ data: DatasetResponse[]; meta: MetaApiResponse }> {
  return http.get(getAgentDataSetsUrls(workspaceId, agentId), params);
}

export function getDataSet(workspaceId: string, agentId: string, dataSetId: string): Promise<DatasetResponse> {
  return http.get(getAgentDataSetUrls(workspaceId, agentId, dataSetId));
}

export function createDataSet(workspaceId: string, agentId: string, payload: any): Promise<any> {
  return http.post(getAgentDataSetsUrls(workspaceId, agentId), payload);
}

export function updateDataSet(workspaceId: string, agentId: string, dataSetId: string, payload: any): Promise<any> {
  return http.put(getAgentDataSetUrls(workspaceId, agentId, dataSetId), payload);
}

export function deleteDataSet(workspaceId: string, agentId: string, id: string): Promise<String> {
  return http.delete(getAgentDataSetUrls(workspaceId, agentId, id));
}

export interface LiveKitTokenResponse {
  access_token: string;
  websocket_url: string;
}

export function getLiveKitToken(agentToken: string, params?: Record<string, any>): Promise<LiveKitTokenResponse> {
  return http.get(getLiveKitTokenUrl(agentToken), params);
}

export interface PublicAgentWidgetConfig {
  name: string;
  avatar_url?: string | null;
  web_embed_configuration?: Record<string, unknown>;
  business_name?: string;
  business_avatar_url?: string | null;
}

export function getPublicAgentWidgetConfig(agentToken: string): Promise<PublicAgentWidgetConfig> {
  return http.get(getPublicAgentWidgetConfigUrl(agentToken));
}

export function getAgentPronunciationRules(
  workspaceId: string,
  agentId: string,
  params?: Record<string, any>
): Promise<{ data: PronunciationRule[]; meta: MetaApiResponse }> {
  return http.get(getAgentPronunciationRulesUrl(workspaceId, agentId), params);
}

export function getAgentPronunciationRule(
  workspaceId: string,
  agentId: string,
  id: number,
  params?: Record<string, any>
): Promise<PronunciationRule> {
  return http.get(getAgentPronunciationRuleUrl(workspaceId, agentId, id), params);
}

export function deleteAgentPronunciationRule(locationId: string, agentId: string, id: number): Promise<String> {
  return http.delete(getAgentPronunciationRuleUrl(locationId, agentId, id));
}

export function updateAgentPronunciationRule(
  workspaceId: string,
  agentId: string,
  id: number,
  payload: any
): Promise<any> {
  return http.put(getAgentPronunciationRuleUrl(workspaceId, agentId, id), payload);
}

export function createAgentPronunciationRule(workspaceId: string, agentId: string, payload: any): Promise<any> {
  return http.post(getAgentPronunciationRulesUrl(workspaceId, agentId), payload);
}
